/**
 * GodConsole — platform administration, gods only (route `/admin`, wrapped in
 * `RequireGod`; every backend route it calls is guarded by `require_god`).
 *
 * Same shell as Settings (back button, title, `SideTabsLayout` rail, white
 * card) so the two admin surfaces look alike.  Tabs:
 *   - Workspaces: every workspace on the platform, drill-down into members
 *     and roles, enter any workspace, create one, and (SaaS) switch
 *     per-workspace features on or off.
 *   - Coupons (SaaS with billing): create, list and revoke prepaid-credit
 *     coupons for one workspace or for all of them.
 *
 * Creator (waiting-list) requests are decided in Settings → Waiting List.
 */
import React, { useCallback } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { SparkIcon } from '@spark';
import { useAuth } from '@/features/auth/AuthContext';
import SideTabsLayout from '@/shared/components/SideTabsLayout.jsx';
import WorkspacesAdmin from './WorkspacesAdmin';
import CouponsAdmin from './CouponsAdmin';
import '@/features/settings/components/settings.css';
import '../styles/admin.css';

export const ADMIN_TITLE = 'Admin Console';
export const WORKSPACES_TAB = { id: 'workspaces', label: 'Workspaces' };
export const COUPONS_TAB = { id: 'coupons', label: 'Coupons' };

/** `?workspace=<id>` keeps the drill-down on reload / back navigation. */
const WORKSPACE_PARAM = 'workspace';

export default function GodConsole() {
    const { isSaas, billingEnabled } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams, setSearchParams] = useSearchParams();

    // Coupons credit prepaid tokens, which only exist with SaaS billing.
    const showCoupons = isSaas === true && billingEnabled === true;
    const tabItems = showCoupons ? [WORKSPACES_TAB, COUPONS_TAB] : [WORKSPACES_TAB];
    const requestedTab = searchParams.get('tab');
    const activeTab = tabItems.some((item) => item.id === requestedTab) ? requestedTab : WORKSPACES_TAB.id;
    const selectedWorkspaceId = activeTab === WORKSPACES_TAB.id ? searchParams.get(WORKSPACE_PARAM) : null;

    const handleTabChange = (nextTab) => {
        const next = new URLSearchParams(searchParams);
        if (nextTab && nextTab !== WORKSPACES_TAB.id) next.set('tab', nextTab);
        else next.delete('tab');
        next.delete(WORKSPACE_PARAM);
        setSearchParams(next, { replace: true });
    };

    const selectWorkspace = useCallback((workspaceId) => {
        setSearchParams((prev) => {
            const next = new URLSearchParams(prev);
            if (workspaceId) next.set(WORKSPACE_PARAM, workspaceId);
            else next.delete(WORKSPACE_PARAM);
            return next;
        });
    }, [setSearchParams]);

    const handleBack = () => {
        const from = location.state?.from;
        if (typeof from === 'string' && from.startsWith('/') && !from.startsWith('/admin')) {
            navigate(from);
            return;
        }
        navigate('/');
    };

    return (
        <div className="settings-page settings admin-console" data-testid="admin-console">
            <button type="button" className="settings-page__back fs-unmask" onClick={handleBack}>
                <SparkIcon slug="arrow-left" size="small" />
                <span>Back</span>
            </button>
            <h1 className="settings-page__title fs-unmask">{ADMIN_TITLE}</h1>
            <SideTabsLayout items={tabItems} activeId={activeTab} onChange={handleTabChange}>
                <div className="settings-card">
                    {activeTab === COUPONS_TAB.id ? (
                        <CouponsAdmin />
                    ) : (
                        <WorkspacesAdmin
                            selectedWorkspaceId={selectedWorkspaceId}
                            onSelectWorkspace={selectWorkspace}
                            featuresEditable={isSaas === true}
                        />
                    )}
                </div>
            </SideTabsLayout>
        </div>
    );
}
